import { useGuideEnabled } from "./lib/guide-preference";
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { BrandMark } from "./components/BrandMark";
import { FailureState } from "./components/FailureState";
import { ThemeToggle } from "./components/ThemeToggle";
import { LandingPage } from "./components/LandingPage";
import { HostedReviewPage } from "./components/HostedReviewPage";
import { AppPageSkeleton, QueueSkeleton } from "./components/LoadingSkeleton";
import { MyWorkPage } from "./components/MyWorkPage";
import { ReviewPage } from "./components/ReviewPage";
import { AnalyticsPage } from "./components/AnalyticsPage";
import { GlobalQueuePage } from "./components/GlobalQueuePage";
import { PersonalQueuePage } from "./components/PersonalQueuePage";
import { HostedAuthenticationRequired, loadFixtureIndex, type FixtureDescriptor } from "./lib/fixtures";
import {
  hostedAuthenticationRequired,
  hostedSession,
  loadHostedQueueSnapshot,
  type HostedQueueSnapshot,
  type OutstandingPull,
  type OutstandingPullStatus,
} from "./lib/hosted";
import { startReviewNotificationPolling } from "./lib/review-notifications";
import { stackMembers } from "./lib/review-stack";
import { localGlobalQueueSnapshot } from "./lib/local-global-queue";
import { guideAuthoringPath, terraformPanelPath } from "../shared/pull-routes";

const AuthorGuidePage = lazy(() => import("./components/AuthorGuidePage").then((module) => ({ default: module.AuthorGuidePage })));
// Only infrastructure pull requests reach this page, so it stays out of the
// bundle every other reviewer downloads.

const localLive2073: FixtureDescriptor = {
  id: "live-2073",
  repository: "Watchful-IP/Watchful",
  pullNumber: 2073,
  title: "Expose Instruction Audit controls",
  authorLogin: "author-2073",
  url: "https://github.com/Watchful-IP/Watchful/pull/2073",
  mode: "live",
  scope: {
    breadth: "focused",
    novelty: "familiar",
    intent: "enhancement",
    stack: {
      id: "instruction-audit-history",
      label: "B6c",
      title: "Instruction Audit history",
      role: "capstone",
      position: 2,
      total: 2,
      contextOwner: "stack",
      reviewAfter: [2124],
    },
  },
  guidePath: "/fixtures/live-2073/guide.yaml",
  filesPath: "/fixtures/live-2073/files.json",
  queue: {
    estimatedMinutes: 15,
    difficulty: "normal",
    areas: ["backend", "security"],
    score: 0,
    reason: "Local representative review fixture",
    reviewGap: "General, backend and security reviews",
  },
  stats: { additions: 567, deletions: 21, changedFiles: 8, commits: 10 },
};

const authMessages: Record<string, string> = {
  github_sso_required: "Your GitHub sign-in worked, but Watchful SSO is not active. Start a Watchful SSO session in GitHub, then retry.",
  github_ip_restricted: "Your GitHub sign-in worked, but Watchful's GitHub network policy blocked the CHEW Worker.",
  github_app_installation_missing: "Your GitHub sign-in worked, but the CHEW App's Watchful installation is not available to this account.",
  github_repository_not_authorized: "Your GitHub sign-in worked, but this account cannot access the Watchful repository through the CHEW App installation.",
  github_forbidden: "Your GitHub sign-in worked, but GitHub denied CHEW access to the Watchful repository.",
  invalid_oauth_state: "That GitHub sign-in expired. Try again.",
};

function localOutstandingPulls(fixtures: FixtureDescriptor[]): OutstandingPull[] {
  const statuses: OutstandingPullStatus[] = ["changes-requested", "ready", "ready", "approved", "draft", "draft"];
  return fixtures.slice(0, 8).map((fixture, index) => ({
    repository: fixture.repository,
    pullNumber: fixture.pullNumber,
    title: fixture.title,
    url: fixture.url ?? `https://github.com/${fixture.repository}/pull/${fixture.pullNumber}`,
    status: statuses[index % statuses.length] ?? "ready",
    requestedReviewers: index % 3 === 0 ? ["reviewer"] : [],
    reviewers: index % 2 === 0 ? ["reviewer"] : [],
    approvers: statuses[index % statuses.length] === "approved" ? ["approver"] : [],
    loc: {
      changedLoc: fixture.stats.additions + fixture.stats.deletions,
      effectiveLoc: fixture.stats.additions + fixture.stats.deletions,
      reviewLoadLoc: Math.round((fixture.stats.additions + fixture.stats.deletions) * (index % 3 === 0 ? 1.25 : .8)),
    },
    updatedAt: new Date(Date.now() - index * 21_600_000).toISOString(),
    createdAt: new Date(Date.now() - (index + 2) * 86_400_000).toISOString(),
    reviewRequestedAt: new Date(Date.now() - index * 25_200_000 - 3_600_000).toISOString(),
    approvedAt: statuses[index % statuses.length] === "approved"
      ? new Date(Date.now() - index * 3_600_000 - 5_400_000).toISOString()
      : undefined,
    approvedBy: statuses[index % statuses.length] === "approved" ? ["approver"] : [],
    changesRequestedAt: statuses[index % statuses.length] === "changes-requested"
      ? new Date(Date.now() - index * 3_600_000 - 7_200_000).toISOString()
      : undefined,
    changesRequestedBy: statuses[index % statuses.length] === "changes-requested" ? ["reviewer"] : [],
    attention: {
      ci: index % 4 === 0
        ? { state: "failure", total: 8, failed: 2, pending: 0 }
        : { state: index % 3 === 0 ? "pending" : "success", total: 8, failed: 0, pending: index % 3 === 0 ? 2 : 0 },
      conversation: {
        issueComments: index % 3,
        reviewThreads: index % 2,
        unresolvedThreads: index % 4 === 0 ? 2 : 0,
        latest: index % 4 === 0 ? {
          author: "reviewer",
          body: "A requested change is waiting for the author.",
          createdAt: new Date(Date.now() - index * 3_600_000).toISOString(),
          url: fixture.url ?? `https://github.com/${fixture.repository}/pull/${fixture.pullNumber}`,
        } : null,
      },
    },
    headSha: fixture.headSha ?? `${index + 1}`.padStart(40, "0"),
    authoring: {
      guideState: (["missing", "current", "draft", "stale", "generating", "failed"] as const)[index % 6]!,
      recommendation: {
        recommended: false,
        required: false,
        reasons: [],
        effectiveChangedLoc: fixture.stats.additions + fixture.stats.deletions,
        excludedChangedLoc: 0,
        completeFileList: true,
      },
      codeSelfReview: "not-started",
      url: fixture.reviewPath ?? `/review/${encodeURIComponent(fixture.id)}`,
      editorUrl: guideAuthoringPath(fixture.repository, fixture.pullNumber),
    },
  }));
}

export default function App() {
  const guideEnabled = useGuideEnabled();
  const localPrototype = ["localhost", "127.0.0.1", "::1"].includes(window.location.hostname);
  const [fixtures, setFixtures] = useState<FixtureDescriptor[]>();
  const [queueSnapshot, setQueueSnapshot] = useState<HostedQueueSnapshot>();
  const [error, setError] = useState<unknown>();
  const [authenticationRequired, setAuthenticationRequired] = useState(false);
  const [sessionChecked, setSessionChecked] = useState(localPrototype);
  // Once the hosted session is confirmed, revalidate in the background on
  // navigation instead of dropping every page to a skeleton (which also
  // discarded page state like the skip-undo affordance).
  const sessionConfirmedRef = useRef(false);
  const [location, setLocation] = useState(() => `${window.location.pathname}${window.location.search}`);
  const pathname = location.split("?")[0] ?? "/";
  const requireAuthentication = useCallback(() => setAuthenticationRequired(true), []);

  useEffect(() => {
    if (pathname.startsWith("/live/") || pathname.startsWith("/author/")) return;
    setError(undefined);
    setAuthenticationRequired(false);
    if (localPrototype) {
      setSessionChecked(true);
      if (!fixtures) {
        void loadFixtureIndex()
          .then((loaded) => setFixtures([...loaded, localLive2073]))
          .catch(setError);
      }
      return;
    }
    if (!sessionConfirmedRef.current) setSessionChecked(false);
    void hostedSession()
      .then(async () => {
        sessionConfirmedRef.current = true;
        setSessionChecked(true);
        if (["/queue", "/operations/queue"].includes(pathname)) {
          // Always adopt the load: the queue pages guard against older data
          // themselves, and holding the first-ever snapshot here would seed
          // every later visit with it.
          setQueueSnapshot(await loadHostedQueueSnapshot());
        }
      })
      .catch((reason: unknown) => {
        if (
          reason instanceof HostedAuthenticationRequired ||
          hostedAuthenticationRequired(reason)
        ) requireAuthentication();
        else setError(reason);
      });
  }, [fixtures, localPrototype, pathname, requireAuthentication]);

  useEffect(() => {
    if (localPrototype) return;
    return startReviewNotificationPolling();
  }, [localPrototype]);

  useEffect(() => {
    const updateLocation = () => setLocation(`${window.location.pathname}${window.location.search}`);
    window.addEventListener("popstate", updateLocation);
    return () => window.removeEventListener("popstate", updateLocation);
  }, []);

  // Terraform was a page of its own; it is a sheet over the review page now.
  // The old route still resolves so a bookmark, or a link already left in a
  // pull-request comment, keeps working — and it now lands where it always
  // should have: on the diff, with the panel over it.
  const terraformMatch = pathname.match(/^\/terraform\/([^/]+)\/([^/]+)\/(\d+)$/);
  if (terraformMatch) {
    const repository = `${decodeURIComponent(terraformMatch[1]!)}/${decodeURIComponent(terraformMatch[2]!)}`;
    const target = terraformPanelPath(repository, Number(terraformMatch[3]));
    // Replaced rather than pushed: Back should return to wherever the reader
    // came from, not to a route that redirects them here again.
    window.history.replaceState({}, "", target);
    return (
      <HostedReviewPage
        key={`${repository}#${terraformMatch[3]}`}
        pullNumber={Number(terraformMatch[3])}
        repository={repository}
      />
    );
  }

  const liveMatch = pathname.match(/^\/live\/([^/]+)\/([^/]+)\/(\d+)$/);
  if (liveMatch) {
    const repository = `${decodeURIComponent(liveMatch[1]!)}/${decodeURIComponent(liveMatch[2]!)}`;
    // Keyed so navigating between PRs remounts with fresh state instead of
    // rendering the previous PR's guide and files under the new URL.
    return (
      <HostedReviewPage
        key={`${repository}#${liveMatch[3]}`}
        pullNumber={Number(liveMatch[3])}
        repository={repository}
      />
    );
  }

  const authorMatch = pathname.match(/^\/author\/([^/]+)\/([^/]+)\/(\d+)$/);
  if (authorMatch && !guideEnabled) {
    return <HostedReviewPage key={`plain-${authorMatch[0]}`} pullNumber={Number(authorMatch[3])} repository={`${decodeURIComponent(authorMatch[1]!)}/${decodeURIComponent(authorMatch[2]!)}`} />;
  }
  if (authorMatch) {
    return (
      <Suspense fallback={<div className="author-guide-loading">Loading guide editor…</div>}>
        <AuthorGuidePage
          pullNumber={Number(authorMatch[3])}
          repository={`${decodeURIComponent(authorMatch[1]!)}/${decodeURIComponent(authorMatch[2]!)}`}
        />
      </Suspense>
    );
  }
  if (error) {
    return (
      <FailureState
        title="CHEW could not load your review queue."
        error={error}
        fallback="The review queue could not be loaded."
      />
    );
  }
  if (authenticationRequired) {
    const authError = new URLSearchParams(window.location.search).get("authError");
    const returnTo = window.location.pathname;
    return (
      <div className="fatal-state hosted-sign-in">
        <div className="hosted-sign-in-brand"><span className="brand"><BrandMark /><strong>CHEW</strong></span><ThemeToggle /></div>
        <strong>Sign in to review Watchful code.</strong>
        <p className={authError ? "auth-error" : undefined}>
          {authError
            ? authMessages[authError] ?? "GitHub sign-in did not complete. Try again, or check the CHEW App installation."
            : "GitHub confirms who you are and that you can access the repository."}
        </p>
        <a className="primary-button" href={`/auth/github?returnTo=${encodeURIComponent(returnTo)}`}>Continue with GitHub</a>
      </div>
    );
  }
  if (!sessionChecked) return <AppPageSkeleton label="Checking your session" />;

  if (!localPrototype && pathname === "/") return <MyWorkPage onAuthenticationRequired={requireAuthentication} />;
  if (!localPrototype && pathname === "/queue") {
    return queueSnapshot ? <PersonalQueuePage initialSnapshot={queueSnapshot} /> : <AppPageSkeleton label="Loading your review queue" />;
  }
  if (!localPrototype && pathname === "/operations/queue") {
    return queueSnapshot ? <GlobalQueuePage initialSnapshot={queueSnapshot} /> : <AppPageSkeleton label="Loading the canonical queue" />;
  }
  if (["/analytics", "/operations/analytics"].includes(pathname)) return <AnalyticsPage />;


  if (!fixtures) return <QueueSkeleton />;
  const localQueue = localGlobalQueueSnapshot(fixtures);
  if (localPrototype && pathname === "/") {
    return <MyWorkPage initialPulls={localOutstandingPulls(fixtures)} initialReviewSnapshot={localQueue} local onAuthenticationRequired={requireAuthentication} />;
  }
  if (localPrototype && pathname === "/queue") return <PersonalQueuePage initialSnapshot={localQueue} local />;
  if (localPrototype && pathname === "/operations/queue") return <GlobalQueuePage initialSnapshot={localQueue} local />;

  const match = pathname.match(/^\/review\/([^/]+)$/);
  if (match) {
    const fixture = fixtures.find((candidate) => candidate.id === match[1]);
    if (fixture) {
      const members = stackMembers(fixture, fixtures);
      const nextMember = members.find((candidate) => candidate.scope.stack.position > fixture.scope.stack.position);
      const prefetchDescriptor = nextMember ?? fixtures
        .filter((candidate) => candidate.id !== fixture.id)
        .sort((left, right) => right.queue.score - left.queue.score)[0];
      return <ReviewPage descriptor={fixture} key={fixture.id} prefetchDescriptor={prefetchDescriptor} stackDescriptors={members} />;
    }
    return <div className="fatal-state"><strong>Unknown fixture.</strong><a href="/">Back to the lab</a></div>;
  }
  return <LandingPage fixtures={fixtures} hosted={false} />;
}
